// "Sign in with Google" — a plain <a> that navigates to the backend's
// /api/auth/google/start endpoint, which generates the state cookie
// and 302s to Google's authorize URL.
//
// Not a <button onClick={...}> because we want the browser to handle
// the redirect natively (preserves cookies, no fetch CORS complications).

import { useEffect, useState } from 'react';

export function GoogleSignInButton({
  label = 'Sign in with Google',
}: {
  label?: string;
}): JSX.Element {
  // Disable when no Google client id is configured (probes /api/auth/me
  // would 401 here; instead we check the env-derived feature flag).
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    // The flag is set by the dev / staging deploy. Default true — only
    // explicit `false` disables; if you want to hide the button when
    // Google isn't wired, set VITE_GOOGLE_OAUTH_ENABLED=false.
    const flag = import.meta.env.VITE_GOOGLE_OAUTH_ENABLED as string | undefined;
    if (flag === 'false') setEnabled(false);
  }, []);

  if (!enabled) return <></>;

  // The backend lives under /api which the Vite dev proxy forwards
  // to the backend port. In prod the portal SPA + backend share the
  // same origin, so /api/auth/google/start is reachable as a relative
  // navigation.
  return (
    <a href="/api/auth/google/start" style={{ textDecoration: 'none' }}>
      <button
        type="button"
        style={{
          width: '100%',
          padding: '10px 16px',
          background: 'white',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius)',
          fontSize: '1em',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          cursor: 'pointer',
        }}
      >
        <GoogleGlyph />
        {label}
      </button>
    </a>
  );
}

function GoogleGlyph(): JSX.Element {
  // Google's brand-guideline-compliant "G" mark. Inline SVG so we
  // don't ship an extra asset.
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.96H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.04l3.007-2.333z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.96L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z"
      />
    </svg>
  );
}
